// src/api/features/bookings/queries.ts

import { useQuery, type QueryKey } from "@tanstack/react-query";

import {
  adminGetBookingById,
  adminGetFlightTabCounts,
  adminGetHotelTabCounts,
  adminGetTourTabCounts,
  adminListBookings,
  adminSearchBookings,
  adminGetTravelPlan,
} from "./api";

import type {
  AdminListBookingsQuery,
  AdminBookingDetailResult,
  PaginatedAdminBookings,
  AdminFlightBookingTabCounts,
  AdminHotelBookingTabCounts,
  AdminTourBookingTabCounts,
  AdminTravelPlanResult,
} from "./types";

/* =========================
 * Query Keys
 * ========================= */

function normalizeListQuery(q: AdminListBookingsQuery = {}) {
  const page = Math.max(1, Number(q.page ?? 1));
  const rawPerPage = Number(q.perPage ?? 10);
  const perPage = Math.max(1, Math.min(100, Number.isFinite(rawPerPage) ? rawPerPage : 10));

  const normStr = (v: any) => {
    const s = String(v ?? "").trim();
    return s ? s : undefined;
  };

  const normNum = (v: any) => {
    if (v === null || v === undefined || v === "") return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  };

  return {
    page,
    perPage,

    q: normStr(q.q),
    status: q.status ?? undefined,
    itemType: q.itemType ?? undefined,

    fromDate: normStr(q.fromDate),
    toDate: normStr(q.toDate),

    tourId: normNum(q.tourId),
    customerUserId: normNum(q.customerUserId),

    sortBy: q.sortBy ?? undefined,
    orderBy: q.orderBy ?? undefined,
  } as const;
}

export const AdminBookingKeys = {
  all: ["adminBookings"] as const,

  listRoot: () => [...AdminBookingKeys.all, "list"] as const,
  list: (q: AdminListBookingsQuery = {}) =>
    [...AdminBookingKeys.listRoot(), normalizeListQuery(q)] as const,

  searchRoot: () => [...AdminBookingKeys.all, "search"] as const,
  search: (q: AdminListBookingsQuery = {}) =>
    [...AdminBookingKeys.searchRoot(), normalizeListQuery(q)] as const,

  tourTabCounts: () => [...AdminBookingKeys.all, "tourTabCounts"] as const,
  flightTabCounts: () => [...AdminBookingKeys.all, "flightTabCounts"] as const,
  hotelTabCounts: () => [...AdminBookingKeys.all, "hotelTabCounts"] as const,

  byIdRoot: () => [...AdminBookingKeys.all, "byId"] as const,
  byId: (id: number | string) => [...AdminBookingKeys.byIdRoot(), String(id)] as const,

  travelPlanRoot: () => [...AdminBookingKeys.all, "travelPlan"] as const,
  travelPlan: (bookingId: number | string) =>
    [...AdminBookingKeys.travelPlanRoot(), String(bookingId)] as const,

  /**
   * Voucher key (optional for future GET endpoint / caching).
   * For now, generate is a mutation and we store snapshot on booking cache,
   * but keeping a key here helps if you add GET /voucher later.
   */
  voucherRoot: () => [...AdminBookingKeys.all, "voucher"] as const,
  voucher: (bookingId: number | string) =>
    [...AdminBookingKeys.voucherRoot(), String(bookingId)] as const,
};

/* =========================
 * Queries (React Query hooks)
 * ========================= */

export function useAdminBookingsList(params: AdminListBookingsQuery) {
  const safeParams = normalizeListQuery(params || {});
  const key: QueryKey = AdminBookingKeys.list(safeParams as any);

  return useQuery<PaginatedAdminBookings, Error>({
    queryKey: key,
    queryFn: () => adminListBookings(safeParams as any),
    placeholderData: (prev) => prev,
    staleTime: 15_000,
    gcTime: 10 * 60_000,
  });
}

export function useAdminBookingsSearch(params: AdminListBookingsQuery) {
  const safeParams = normalizeListQuery(params || {});
  const key: QueryKey = AdminBookingKeys.search(safeParams as any);

  return useQuery<PaginatedAdminBookings, Error>({
    queryKey: key,
    queryFn: () => adminSearchBookings(safeParams as any),
    placeholderData: (prev) => prev,
    staleTime: 10_000,
    gcTime: 10 * 60_000,
  });
}

export function useAdminTourTabCounts(enabled = true) {
  const key: QueryKey = AdminBookingKeys.tourTabCounts();

  return useQuery<AdminTourBookingTabCounts, Error>({
    queryKey: key,
    queryFn: () => adminGetTourTabCounts(),
    enabled,
    staleTime: 15_000,
    gcTime: 10 * 60_000,
  });
}

export function useAdminFlightTabCounts(enabled = true) {
  const key: QueryKey = AdminBookingKeys.flightTabCounts();

  return useQuery<AdminFlightBookingTabCounts, Error>({
    queryKey: key,
    queryFn: () => adminGetFlightTabCounts(),
    enabled,
    staleTime: 15_000,
    gcTime: 10 * 60_000,
  });
}

export function useAdminHotelTabCounts(enabled = true) {
  return useQuery<AdminHotelBookingTabCounts, Error>({
    queryKey: AdminBookingKeys.hotelTabCounts(),
    queryFn: () => adminGetHotelTabCounts(),
    enabled,
    staleTime: 15_000,
    gcTime: 10 * 60_000,
  });
}

export function useAdminBookingById(id: number | string | null | undefined) {
  const enabled = id !== null && id !== undefined && String(id).trim() !== "";

  const key: QueryKey = enabled
    ? AdminBookingKeys.byId(String(id))
    : [...AdminBookingKeys.byIdRoot(), "disabled"];

  return useQuery<AdminBookingDetailResult, Error>({
    queryKey: key,
    queryFn: () => adminGetBookingById(String(id)),
    enabled,
    staleTime: 15_000,
    gcTime: 10 * 60_000,
  });
}

export function useAdminTravelPlan(bookingId: number | string | null | undefined) {
  const enabled =
    bookingId !== null &&
    bookingId !== undefined &&
    String(bookingId).trim() !== "";

  const key: QueryKey = enabled
    ? AdminBookingKeys.travelPlan(String(bookingId))
    : [...AdminBookingKeys.travelPlanRoot(), "disabled"];

  return useQuery<AdminTravelPlanResult, Error>({
    queryKey: key,
    queryFn: async () => {
      const plan = await adminGetTravelPlan(String(bookingId));

      // ✅ backend-aligned: ONLY these fields (no pickup/dropoff/notes)
      return {
        ...(plan as any),
        bookingId: Number((plan as any)?.bookingId ?? bookingId),
        status: (plan as any)?.status ?? ("NOT_SHARED" as any),
        segmentsJson: Array.isArray((plan as any)?.segmentsJson)
          ? (plan as any).segmentsJson
          : [],
      } as AdminTravelPlanResult;
    },
    enabled,
    staleTime: 15_000,
    gcTime: 10 * 60_000,
  });
}
